import { getFlightBookingFromChange } from "./getRequestedDateChange";

const text = (value) => String(value ?? "").trim();

const ymd = (value) => {
  if (!value) return "";
  const raw = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString().slice(0, 10);
};

export function travellerName(traveller) {
  return [traveller?.title, traveller?.firstName, traveller?.middleName, traveller?.lastName]
    .map(text)
    .filter(Boolean)
    .join(" ");
}

function fieldValue(traveller, field) {
  switch (field) {
    case "birthDate":
      return ymd(traveller?.birthDate);
    case "expiryDate":
      return ymd(traveller?.document?.expiryDate);
    case "nationality":
      return text(traveller?.document?.nationality);
    case "number":
      return text(traveller?.document?.number);
    default:
      return text(traveller?.[field]);
  }
}

const KIND_FIELDS = {
  name: ["title", "firstName", "middleName", "lastName"],
  gender: ["gender"],
  birthDate: ["birthDate"],
  document: ["nationality", "number", "expiryDate"],
};

const KIND_LABELS = {
  name: "Name",
  gender: "Gender",
  birthDate: "Date of birth",
  document: "Passport or ID",
};

const KIND_ORDER = ["name", "gender", "birthDate", "document"];

function kindMoved(before, after, kind) {
  return (KIND_FIELDS[kind] || []).some(
    (field) => fieldValue(before, field) !== fieldValue(after, field),
  );
}

/**
 * What a PassengerInfo / details-change request is asking to correct —
 * for the admin Details Change queue (replaces the blank "new travel date").
 */
export function getDetailsChangeSummary(request) {
  const booking = getFlightBookingFromChange(request) || {};
  const oldList = Array.isArray(booking?.travelers) ? booking.travelers : [];
  const changedBooking =
    request?.changedFlightBooking?.at?.(0) || request?.changedFlightBooking;
  const newList = Array.isArray(request?.passengerInfo)
    ? request.passengerInfo
    : Array.isArray(changedBooking?.travelers)
      ? changedBooking.travelers
      : [];

  const found = new Set();
  const lines = [];
  let nameSnippet = "";

  newList.forEach((after, index) => {
    if (!after) return;
    const before =
      oldList.find((t) => t?.id != null && after?.id != null && t.id === after.id) ||
      oldList.find(
        (t) => t?._id && after?._id && String(t._id) === String(after._id),
      ) ||
      oldList[index] ||
      {};
    KIND_ORDER.forEach((kind) => {
      if (!kindMoved(before, after, kind)) return;
      found.add(kind);
      if (kind === "name" && !nameSnippet) {
        const from = travellerName(before);
        const to = travellerName(after);
        if (from && to && from !== to) nameSnippet = `${from} → ${to}`;
        else if (to) nameSnippet = to;
      }
    });
  });

  const labels = KIND_ORDER.filter((kind) => found.has(kind)).map(
    (kind) => KIND_LABELS[kind],
  );
  if (nameSnippet) lines.push(nameSnippet);
  labels
    .filter((label) => label !== "Name")
    .forEach((label) => lines.push(label));

  return {
    labels,
    label: labels.join(" · ") || (newList.length ? "Traveller details" : "—"),
    detail: nameSnippet,
    lines: lines.length ? lines : [labels.join(" · ") || "Traveller details"],
  };
}
